import React, {
    useState, useRef, useEffect, useContext,
    useCallback,
} from "react";
import { FormValidateContext } from "@root/context/form-validate";
import Tooltip from "@root/components/tooltip";
import ResponsiveWrapper from "../_responsive-wrapper";
import Icon from "../Icon/icon";
import formatFileSize from "../../helpers/formatFileSize";
import allowedFormats from "../../helpers/allowedFormats";
import Button from "../button";
import PopUp from "../pop-up";
import PopUpTypes from "../../constants/popUpTypes";
import getFormatCatergory from "@helpers/get-format-category";

const { v4: uuidv4 } = require("uuid");

const FileUpload = ({ multiple, formats, mbLimit = 20, onChange, required, defaultValue = [], isEditing = true }) => {
    const [uuid] = useState(uuidv4());
    const isMultiple = !!multiple;
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState([]);
    const [showPopUp, setPopUp] = useState(false);
    const [popUpMessage, setPopUpMessage] = useState(null);
    const [popUpType, setPopUpType] = useState(null);
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);
    const { registerField, unregisterField, getFieldError, clearFieldError } = useContext(FormValidateContext) || {};

    const validate = useCallback(() => {
        if (required && files.length === 0) {
            return "Please upload an image!";
        }
        return true;
    }, [required, files]);

    useEffect(() => {
        registerField?.({
            id: uuid,
            validate,
            ref: dropZoneRef,
        });
        return () => {
            unregisterField?.({ id: uuid });
        };
    }, [validate, files]);

    useEffect(() => {
        if (Array.isArray(defaultValue) && defaultValue.length > 0) {
            const initializedFiles = defaultValue.map((file) => {
                if (typeof file === "string") {
                    return { name: file.split("/").pop(), url: file };
                } if (file instanceof File) {
                    return file;
                } if (file && file.name && file.url) {
                    return file;
                }
                return null;
            }).filter(Boolean);
            setFiles(initializedFiles);
        }
    }, [defaultValue]);

    const fieldError = getFieldError?.(uuid);

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dropZoneRef.current && dropZoneRef.current.contains(event.target)) {
            setDragging(true);
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (dropZoneRef.current && !dropZoneRef.current.contains(event.relatedTarget)) {
            setDragging(false);
        }
    };

    const updateFiles = (updatedFiles) => {
        setFiles(updatedFiles);
        clearFieldError(uuid);
        const dataTransfer = new DataTransfer();
        updatedFiles.forEach((file) => {
            if(file instanceof File) {
                dataTransfer.items.add(file);
            }
        });
        if (fileInputRef.current) {
            fileInputRef.current.files = dataTransfer.files;
        }
        fileInputRef.current.value = "";

        updatedFiles = updatedFiles.map((file) => {
            if (file?.url) {
                return file?.url;
            }
            return file;
        })

        onChange(updatedFiles);
    };

    const handleFileUpdate = (event, isDropping) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(false);
        setPopUp(false);
        setPopUpMessage(null);
        setPopUpType(null);

        const newFiles = isDropping ? event.dataTransfer.files : event.target.files;

        let updatedFiles = isMultiple ? [...files, ...Array.from(newFiles)] : [newFiles[newFiles.length - 1]];
        const filteredFiles = updatedFiles.filter((file, index, self) =>
            index === self.findIndex((f) => f?.name === file?.name && f?.size === file.size));
        if (filteredFiles.length < updatedFiles.length) {
            setPopUp(true);
            setPopUpType(PopUpTypes.ERROR);
            setPopUpMessage(<p>Duplicate files are not allowed!</p>);
            return;
        }
        updatedFiles = filteredFiles;
        const exceedsSizeLimit = updatedFiles.some((file) => file?.size > mbLimit * 1024 * 1024);
        if (exceedsSizeLimit) {
            const offendingFile = updatedFiles.find((file) => file?.size > mbLimit * 1024 * 1024);
            setPopUp(true);
            setPopUpType(PopUpTypes.ERROR);
            setPopUpMessage(<p>{`File ${offendingFile.name} size exceeds the limit of ${mbLimit} MB!`}</p>);
            return;
        }
        const invalidFiles = updatedFiles.filter((file) => !formats.includes(file?.type) && !file?.url);
        if (invalidFiles.length > 0) {
            setPopUp(true);
            setPopUpType(PopUpTypes.INFORMATION);
            setPopUpMessage(<p>Unsupported file type!</p>);
            updatedFiles = updatedFiles.filter((file) => formats.includes(file?.type));
        }
        updateFiles(updatedFiles);
    };

    const handleFileDelete = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        updateFiles(newFiles);
    };

    const handleButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <ResponsiveWrapper className={`file-upload ${files.length >= 1 && !isMultiple ? "file-upload__hasContent" : null}`}>
            <PopUp type={popUpType} show={showPopUp} onClick={() => setPopUp(false)}>{popUpMessage}</PopUp>
            <div
                ref={dropZoneRef}
                className={`file-upload__inner ${dragging ? "file-upload__inner--dragging" : ""} ${fieldError ? "file-upload__inner--invalid" : ""}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleFileUpdate(event, true)}
            >
                <div className="file-upload__info">
                    <div className="file-upload__icon"><Icon icon="upload" /></div>
                    <p className="file-upload__text">
                        {isMultiple ? "Select files or drag and drop here" : "Select a file or drag and drop here"}
                    </p>
                    <p className="file-upload__allowed-formats">
                        {`${allowedFormats(formats)} ${isMultiple ? "files" : "file"} size no more than ${mbLimit}MB`}
                    </p>
                </div>
                <label htmlFor={uuid} className="file-upload__input-label">
                    <Button variant="secondary" size="large" onClick={handleButtonClick}>
                        {isMultiple ? "Select Files" : "Select File"}
                    </Button>
                    <input
                        id={uuid}
                        type="file"
                        className="file-upload__input"
                        ref={fileInputRef}
                        onChange={(event) => handleFileUpdate(event, false)}
                        multiple={isMultiple}
                    />
                </label>
            </div>
            <div className="file-upload__list">
                {files.map((file, index) => (
                    <div key={`${file?.name}-${file?.size || index}`} className="file-upload__file">
                        <div className="file-upload__file-details">
                            <div className="file-upload__file-preview">
                                {file?.url ? (
                                    <img className="file-upload__file-image" src={file.url} alt={file?.name} />
                                ) : getFormatCatergory(file?.type) === "IMAGE" ? (
                                    <img className="file-upload__file-image" src={URL.createObjectURL(file)} alt={file?.name} />
                                ) : (
                                    <Icon className="file-upload__file-icon" icon="file-text" />
                                )}
                            </div>
                            <div className="file-upload__file-text">
                                <p className="file-upload__file-name">{file?.name}</p>
                                {!isEditing && (
                                    <p className="file-upload__file-size">{formatFileSize(file?.size)}</p>
                                )}
                            </div>
                        </div>
                        <div className="file-upload__file-delete">
                            <button
                                type="button"
                                className="file-upload__file-delete-btn"
                                onClick={() => handleFileDelete(index)}
                                aria-label={`Delete ${file?.name}`}
                            >
                                <Icon icon="trash-2" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <Tooltip error={fieldError ? fieldError?.message : false} />
        </ResponsiveWrapper>
    );
};

export default FileUpload;
